import './style.css'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { FeatureCollection, Geometry } from 'geojson'

type Mode = 'city' | 'migration' | 'flows'

type Meta = {
  brand: string
  product: string
  host: string
  layers: Record<string, { years: number[]; note?: string }>
  fo_centroids: Record<string, [number, number]>
  warnings: string[]
  geography: { missing_on_map: string[]; note: string }
}

type RegionMetric = {
  region: string
  value: number | null
  [key: string]: unknown
}

type FlowYear = {
  year: number
  flows: { source: string; target: string; value: number }[]
  nodes: string[]
}

type TimelinePoint = {
  year: number
  arrivals: number | null
  departures: number | null
  net: number | null
}

type CityPoint = {
  id: string
  city: string
  region: string
  lat: number
  lon: number
  population: number
  change_abs: number | null
  change_pct: number | null
}

const state = {
  mode: 'migration' as Mode,
  year: 2023,
  playing: false,
  selected: null as string | null,
}

let map: L.Map
let meta: Meta
let cityByYear: Record<string, RegionMetric[]>
let migrationByYear: Record<string, RegionMetric[]>
let foFlows: Record<string, FlowYear>
let timeline: TimelinePoint[]
let subjects: Record<string, any>
let cityPointsByYear: Record<string, CityPoint[]>
let regionsGeo: FeatureCollection
let regionsLayer: L.GeoJSON | null = null
let citiesLayer: L.LayerGroup | null = null
let flowsLayer: L.LayerGroup | null = null
let playTimer: number | null = null
let particleAnim: ParticleAnimation | null = null

const fmt = new Intl.NumberFormat('ru-RU')
const fmtSigned = (n: number | null | undefined) => {
  if (n == null || Number.isNaN(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return sign + fmt.format(Math.round(n))
}

async function loadJSON<T>(path: string): Promise<T> {
  const res = await fetch(path)
  if (!res.ok) throw new Error(`Failed to load ${path}`)
  return res.json()
}

function yearsForMode(mode: Mode): number[] {
  if (mode === 'city') return meta.layers.city_population_change.years
  if (mode === 'migration') return meta.layers.migration_net.years
  return meta.layers.fo_flows.years
}

function yearRangeLabel(mode: Mode): string {
  const years = yearsForMode(mode)
  return `${years[0]}–${years[years.length - 1]}`
}

function clampYear(mode: Mode, year: number): number {
  const years = yearsForMode(mode)
  if (years.includes(year)) return year
  return years.reduce((best, y) => (Math.abs(y - year) < Math.abs(best - year) ? y : best), years[0])
}

function currentMetrics(): RegionMetric[] {
  if (state.mode === 'city') return cityByYear[String(state.year)] || []
  if (state.mode === 'migration') return migrationByYear[String(state.year)] || []
  return []
}

function scaleLimits(values: number[]): { min: number; max: number } {
  const abs = values.map((v) => Math.abs(v)).filter((v) => Number.isFinite(v)).sort((a, b) => a - b)
  if (!abs.length) return { min: -1, max: 1 }
  const p90 = abs[Math.floor(abs.length * 0.9)] || abs[abs.length - 1]
  const max = Math.max(p90, 1)
  return { min: -max, max }
}

function colorForValue(v: number | null | undefined, min: number, max: number): string {
  if (v == null || !Number.isFinite(v)) return '#455868'
  if (v === 0) return '#455868'
  const t = Math.max(-1, Math.min(1, v / (v < 0 ? Math.abs(min) : max)))
  // Lift weak regions so Far East stays readable on the dark basemap
  const lifted = Math.sign(t) * Math.max(0.22, Math.pow(Math.abs(t), 0.38))
  if (lifted >= 0) return lerpColor('#455868', '#5dffd0', lifted)
  return lerpColor('#455868', '#ff8f5c', Math.abs(lifted))
}

function lerpColor(a: string, b: string, t: number): string {
  const pa = hexToRgb(a)
  const pb = hexToRgb(b)
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t)
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t)
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t)
  return `rgb(${r},${g},${bl})`
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

function curveLonLat(a: [number, number], b: [number, number], lift: number): [number, number][] {
  // a,b are [lon,lat]; returns leaflet [lat,lon] points
  const mx = (a[0] + b[0]) / 2
  const my = (a[1] + b[1]) / 2
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const cx = mx - dy * lift
  const cy = my + dx * lift * 0.35
  const pts: [number, number][] = []
  for (let i = 0; i <= 40; i++) {
    const t = i / 40
    const x = (1 - t) * (1 - t) * a[0] + 2 * (1 - t) * t * cx + t * t * b[0]
    const y = (1 - t) * (1 - t) * a[1] + 2 * (1 - t) * t * cy + t * t * b[1]
    pts.push([y, x])
  }
  return pts
}

type ParticlePath = {
  points: [number, number][] // latlon
  speed: number
  size: number
  label: string
}

class ParticleAnimation {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private map: L.Map
  private paths: ParticlePath[] = []
  private particles: { pathIndex: number; t: number; speed: number }[] = []
  private raf = 0
  private running = false

  constructor(map: L.Map) {
    this.map = map
    this.canvas = L.DomUtil.create('canvas', 'particle-layer') as HTMLCanvasElement
    const pane = map.getPanes().overlayPane
    pane.appendChild(this.canvas)
    this.ctx = this.canvas.getContext('2d')!
    this.resize()
    map.on('move zoom resize', this.redraw, this)
    map.on('zoomanim', this.resize, this)
  }

  setPaths(paths: ParticlePath[]) {
    this.paths = paths
    this.particles = []
    for (let i = 0; i < paths.length; i++) {
      const p = paths[i]
      // Stronger contrast: weak flows few dots, strong flows many
      const count = Math.max(4, Math.min(48, Math.round(2 + Math.pow(p.size, 2.2) * 55)))
      for (let k = 0; k < count; k++) {
        this.particles.push({
          pathIndex: i,
          t: Math.random(),
          speed: p.speed * (0.75 + Math.random() * 0.55),
        })
      }
    }
  }

  start() {
    if (this.running) return
    this.running = true
    this.canvas.style.display = 'block'
    const tick = () => {
      if (!this.running) return
      for (const p of this.particles) {
        p.t += p.speed
        if (p.t > 1) p.t -= 1
      }
      this.redraw()
      this.raf = requestAnimationFrame(tick)
    }
    this.raf = requestAnimationFrame(tick)
  }

  stop() {
    this.running = false
    cancelAnimationFrame(this.raf)
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.canvas.style.display = 'none'
  }

  destroy() {
    this.stop()
    this.map.off('move zoom resize', this.redraw, this)
    this.canvas.remove()
  }

  private resize = () => {
    const size = this.map.getSize()
    this.canvas.width = size.x
    this.canvas.height = size.y
    this.canvas.style.width = `${size.x}px`
    this.canvas.style.height = `${size.y}px`
    const topLeft = this.map.containerPointToLayerPoint([0, 0])
    L.DomUtil.setPosition(this.canvas, topLeft)
    this.redraw()
  }

  private redraw = () => {
    const ctx = this.ctx
    const size = this.map.getSize()
    const topLeft = this.map.containerPointToLayerPoint([0, 0])
    L.DomUtil.setPosition(this.canvas, topLeft)
    ctx.clearRect(0, 0, size.x, size.y)
    if (!this.running || !this.paths.length) return

    // faint trails
    for (const path of this.paths) {
      ctx.beginPath()
      path.points.forEach((ll, i) => {
        const pt = this.map.latLngToContainerPoint(ll)
        if (i === 0) ctx.moveTo(pt.x, pt.y)
        else ctx.lineTo(pt.x, pt.y)
      })
      ctx.strokeStyle = 'rgba(255, 179, 107, 0.18)'
      ctx.lineWidth = 1 + path.size * 3
      ctx.stroke()
    }

    for (const p of this.particles) {
      const path = this.paths[p.pathIndex]
      if (!path) continue
      const idx = Math.min(path.points.length - 1, Math.floor(p.t * (path.points.length - 1)))
      const next = Math.min(path.points.length - 1, idx + 1)
      const localT = p.t * (path.points.length - 1) - idx
      const a = path.points[idx]
      const b = path.points[next]
      const lat = a[0] + (b[0] - a[0]) * localT
      const lon = a[1] + (b[1] - a[1]) * localT
      const pt = this.map.latLngToContainerPoint([lat, lon])
      const r = 0.9 + path.size * 1.1
      const glow = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r * 2.4)
      glow.addColorStop(0, 'rgba(255, 230, 180, 0.9)')
      glow.addColorStop(0.4, 'rgba(255, 179, 107, 0.55)')
      glow.addColorStop(1, 'rgba(255, 179, 107, 0)')
      ctx.fillStyle = glow
      ctx.beginPath()
      ctx.arc(pt.x, pt.y, r * 2.4, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#ffe6b4'
      ctx.beginPath()
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

function renderShell() {
  const app = document.querySelector('#app')!
  app.innerHTML = `
    <div id="map"></div>
    <div class="tooltip" id="tooltip"></div>
    <div class="shell">
      <header class="hero">
        <div class="brand">
          <p class="brand-mark">TARKO</p>
          <p class="brand-sub">Atlas of Migration — интерактивная карта населения и потоков по России</p>
          <div class="brand-meta">
            <span class="chip">migration.tarko.su</span>
            <span class="chip" id="chipRange">годы зависят от режима</span>
          </div>
        </div>
        <div class="modes">
          <a class="data-btn" href="/dannye.html">Скачать данные</a>
          <div class="mode-switch" role="tablist" aria-label="Режим карты">
            <button data-mode="city" type="button">Города</button>
            <button data-mode="migration" type="button" class="active">Миграция</button>
            <button data-mode="flows" type="button">Потоки</button>
          </div>
          <div class="legend">
            <span>убыль</span>
            <div class="legend-bar" aria-hidden="true"></div>
            <span>прибыль</span>
          </div>
        </div>
      </header>
      <div class="center-stage">
        <aside class="detail" id="detail"></aside>
      </div>
      <footer class="dock">
        <div class="dock-panel">
          <button class="play" id="play" type="button" aria-label="Проиграть годы">▶</button>
          <div class="year-wrap">
            <div class="year-row">
              <div class="year-label" id="yearLabel">2023</div>
              <div class="year-hint" id="yearHint"></div>
            </div>
            <input id="year" type="range" min="0" max="0" step="1" value="0" />
          </div>
          <div class="spark" id="spark" aria-hidden="true"></div>
        </div>
        <p class="footnote" id="footnote"></p>
      </footer>
    </div>
  `
}

function syncControls() {
  const years = yearsForMode(state.mode)
  state.year = clampYear(state.mode, state.year)
  const idx = years.indexOf(state.year)
  const slider = document.querySelector('#year') as HTMLInputElement
  slider.min = '0'
  slider.max = String(years.length - 1)
  slider.value = String(idx)
  ;(document.querySelector('#yearLabel') as HTMLElement).textContent = String(state.year)

  document.querySelectorAll('.mode-switch button').forEach((btn) => {
    btn.classList.toggle('active', (btn as HTMLElement).dataset.mode === state.mode)
  })

  const hints: Record<Mode, string> = {
    city: `Города на карте · изменение по регионам · доступно ${yearRangeLabel('city')}`,
    migration: `Миграционный прирост регионов · доступно ${yearRangeLabel('migration')}`,
    flows: `Переезды между округами · точки летят по направлению · ${yearRangeLabel('flows')}`,
  }
  ;(document.querySelector('#yearHint') as HTMLElement).textContent = hints[state.mode]
  ;(document.querySelector('#chipRange') as HTMLElement).textContent =
    state.mode === 'city'
      ? `города ${yearRangeLabel('city')}`
      : state.mode === 'migration'
        ? `миграция регионов ${yearRangeLabel('migration')}`
        : `потоки ${yearRangeLabel('flows')} · ряд РФ с 1990 в графике`

  const notes: Record<Mode, string> = {
    city: 'Цвет региона — сумма изменений городов. Кружки — сами города (размер = население, цвет = рост/убыль).',
    migration: meta.layers.migration_net.note || '',
    flows: 'Точки летят откуда приехали → куда уехали. Чем сильнее поток, тем больше точек. Это крупные округа России, не отдельные области.',
  }
  ;(document.querySelector('#footnote') as HTMLElement).textContent =
    [notes[state.mode], meta.geography.note].filter(Boolean).join(' ')

  renderSpark()
  applyLayers()
  renderDetail(state.selected)
}

function renderSpark() {
  const el = document.querySelector('#spark')!
  if (!timeline.length) {
    el.innerHTML = ''
    return
  }
  const w = 220
  const h = 48
  const pad = 4
  const nets = timeline.filter((t) => t.net != null) as (TimelinePoint & { net: number })[]
  const min = Math.min(...nets.map((t) => t.net))
  const max = Math.max(...nets.map((t) => t.net))
  const x = (i: number) => pad + (i / (nets.length - 1)) * (w - pad * 2)
  const y = (v: number) => {
    const t = (v - min) / (max - min || 1)
    return h - pad - t * (h - pad * 2)
  }
  const d = nets.map((t, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(t.net).toFixed(1)}`).join(' ')
  const zero = y(0)
  el.innerHTML = `<svg viewBox="0 0 ${w} ${h}" role="img" aria-label="Чистая миграция РФ 1990–2023">
    <line x1="${pad}" x2="${w - pad}" y1="${zero}" y2="${zero}" stroke="rgba(215,224,231,0.2)" stroke-dasharray="3 3"/>
    <path d="${d}" fill="none" stroke="#7ec8c4" stroke-width="2"/>
  </svg>`
}

function closeDetail() {
  state.selected = null
  renderDetail(null)
}

function detailShell(title: string, subtitle: string, body: string): string {
  return `
    <button type="button" class="detail-close" aria-label="Закрыть">×</button>
    <h2>${title}</h2>
    <p class="muted">${subtitle}</p>
    ${body}
  `
}

function bindDetailClose(el: HTMLElement) {
  el.querySelector('.detail-close')?.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    closeDetail()
  })
}

function renderDetail(region: string | null) {
  const el = document.querySelector('#detail') as HTMLElement
  if (!region) {
    el.classList.remove('open')
    el.innerHTML = ''
    return
  }

  if (region.startsWith('city:')) {
    const cityId = region.slice(5)
    const cities = cityPointsByYear[String(state.year)] || []
    const city = cities.find((c) => c.id === cityId)
    if (!city) {
      el.classList.remove('open')
      return
    }
    const cls = city.change_abs == null ? '' : city.change_abs >= 0 ? 'pos' : 'neg'
    el.classList.add('open')
    el.innerHTML = detailShell(
      city.city,
      `${city.region} · ${state.year}`,
      `<div class="stat-grid">
        <div class="stat"><span class="label">Население</span><span class="value">${fmt.format(city.population)}</span></div>
        <div class="stat"><span class="label">Изменение за год</span><span class="value ${cls}">${fmtSigned(city.change_abs)}</span></div>
        <div class="stat"><span class="label">Изменение %</span><span class="value ${cls}">${city.change_pct == null ? '—' : `${city.change_pct > 0 ? '+' : ''}${city.change_pct.toFixed(2)}%`}</span></div>
      </div>`,
    )
    bindDetailClose(el)
    return
  }

  const sub = subjects[region]
  const metric = currentMetrics().find((m) => m.region === region)
  const city = sub?.city_years?.[String(state.year)]
  const mig = sub?.migration_years?.[String(state.year)]
  const comp = sub?.components_2024

  if (state.mode === 'flows') {
    el.classList.add('open')
    el.innerHTML = detailShell(
      region,
      `Крупный округ России · точки показывают направление переездов · ${state.year}`,
      `<div class="stat-grid">
        <div class="stat"><span class="label">Год</span><span class="value">${state.year}</span></div>
        <div class="stat"><span class="label">Уровень</span><span class="value">между округами</span></div>
      </div>`,
    )
    bindDetailClose(el)
    return
  }

  const primary = metric?.value ?? null
  const primaryLabel = state.mode === 'city' ? 'Изменение городов' : 'Миграц. прирост'
  const cls = primary == null ? '' : primary >= 0 ? 'pos' : 'neg'
  el.classList.add('open')
  el.innerHTML = detailShell(
    region,
    `${state.mode === 'city' ? 'Слой городов' : 'Слой миграции'} · ${state.year}`,
    `<div class="stat-grid">
      <div class="stat"><span class="label">${primaryLabel}</span><span class="value ${cls}">${fmtSigned(primary as number | null)}</span></div>
      <div class="stat"><span class="label">Городов в панели</span><span class="value">${city?.cities ?? '—'}</span></div>
      <div class="stat"><span class="label">Население городов</span><span class="value">${city ? fmt.format(city.city_population) : '—'}</span></div>
      <div class="stat"><span class="label">Прибыло / выбыло</span><span class="value">${mig ? `${fmt.format(mig.arrivals)} / ${fmt.format(mig.departures)}` : state.year === 2024 && comp ? 'оценка 2024' : '—'}</span></div>
      <div class="stat"><span class="label">Межрегиональный нетто</span><span class="value ${mig && mig.inter_region_net >= 0 ? 'pos' : 'neg'}">${mig ? fmtSigned(mig.inter_region_net) : '—'}</span></div>
      <div class="stat"><span class="label">Международный нетто</span><span class="value ${mig && mig.international_net >= 0 ? 'pos' : 'neg'}">${mig ? fmtSigned(mig.international_net) : comp ? fmtSigned(comp.migration_net) : '—'}</span></div>
    </div>`,
  )
  bindDetailClose(el)
}

function applyLayers() {
  const tip = document.querySelector('#tooltip') as HTMLElement
  const metrics = currentMetrics()
  const byRegion = new Map(metrics.map((m) => [m.region, m]))
  const values = metrics.map((m) => Number(m.value)).filter((v) => Number.isFinite(v))
  const { min, max } = scaleLimits(values)

  if (regionsLayer) {
    map.removeLayer(regionsLayer)
    regionsLayer = null
  }
  if (citiesLayer) {
    map.removeLayer(citiesLayer)
    citiesLayer = null
  }
  if (flowsLayer) {
    map.removeLayer(flowsLayer)
    flowsLayer = null
  }
  if (particleAnim) particleAnim.stop()

  const choropleth = state.mode !== 'flows'
  regionsLayer = L.geoJSON(regionsGeo as FeatureCollection<Geometry>, {
    style: (feat) => {
      const region = feat?.properties?.canonical_region as string | undefined
      const metric = region ? byRegion.get(region) : undefined
      const value = metric?.value == null ? null : Number(metric.value)
      return {
        color: '#c5d2de',
        weight: 1.15,
        fillColor: choropleth ? colorForValue(value, min, max) : '#334553',
        fillOpacity: choropleth ? (state.mode === 'city' ? 0.62 : 0.92) : 0.35,
      }
    },
    onEachFeature: (feat, layer) => {
      const region = feat.properties?.canonical_region as string | undefined
      const name = feat.properties?.display_name || feat.properties?.name
      layer.on({
        add: () => {
          const el = (layer as L.Path).getElement()
          if (el) el.setAttribute('data-region', String(name || ''))
        },
        mouseover: (e) => {
          const value = region ? byRegion.get(region)?.value : null
          ;(e.target as L.Path).setStyle({ weight: 2, color: '#f3f7fa' })
          tip.innerHTML = `<strong>${name}</strong><br>${choropleth ? fmtSigned(value == null ? null : Number(value)) : 'фон для пауков ФО'}`
          tip.classList.add('show')
        },
        mousemove: (e) => {
          const oe = e.originalEvent as MouseEvent
          tip.style.left = `${oe.clientX + 12}px`
          tip.style.top = `${oe.clientY + 12}px`
        },
        mouseout: (e) => {
          regionsLayer?.resetStyle(e.target)
          tip.classList.remove('show')
        },
        click: () => {
          state.selected = region || null
          renderDetail(state.selected)
        },
      })
    },
  }).addTo(map)

  if (state.mode === 'city') {
    const points = cityPointsByYear[String(state.year)] || []
    // Show top cities + all with |change| large; cap for performance
    const visible = points.slice(0, 350)
    const maxPop = Math.max(...visible.map((c) => c.population), 1)
    const changeVals = visible.map((c) => c.change_abs || 0)
    const lim = scaleLimits(changeVals)
    citiesLayer = L.layerGroup().addTo(map)
    for (const c of visible) {
      const radius = 3 + Math.sqrt(c.population / maxPop) * 16
      const fill = colorForValue(c.change_abs, lim.min, lim.max)
      const marker = L.circleMarker([c.lat, c.lon], {
        radius,
        color: 'rgba(16,24,32,0.75)',
        weight: 1,
        fillColor: fill,
        fillOpacity: 0.9,
        bubblingMouseEvents: false,
      })
      marker.bindTooltip(
        `<strong>${c.city}</strong><br>${fmt.format(c.population)} чел.<br>${fmtSigned(c.change_abs)}`,
        { direction: 'top', offset: [0, -4] },
      )
      marker.on('click', () => {
        state.selected = `city:${c.id}`
        renderDetail(state.selected)
      })
      citiesLayer.addLayer(marker)
    }
  }

  if (state.mode === 'flows') {
    flowsLayer = L.layerGroup().addTo(map)
    const pack = foFlows[String(state.year)]
    const paths: ParticlePath[] = []
    if (pack) {
      const maxFlow = Math.max(...pack.flows.map((f) => f.value), 1)
      const sorted = [...pack.flows].sort((a, b) => a.value - b.value)
      for (const f of sorted) {
        const from = meta.fo_centroids[f.source]
        const to = meta.fo_centroids[f.target]
        if (!from || !to) continue
        const t = f.value / maxFlow
        // Emphasize difference between weak and strong flows
        const weight = Math.pow(t, 1.6)
        const latlngs = curveLonLat(from, to, 0.3)
        paths.push({
          points: latlngs,
          speed: 0.0022 + weight * 0.005,
          size: 0.12 + weight * 0.95,
          label: `${f.source} → ${f.target}`,
        })
        const end = latlngs[latlngs.length - 1]
        const prev = latlngs[Math.max(0, latlngs.length - 4)]
        const arrow = L.polyline([prev, end], {
          color: '#ffb36b',
          weight: 1 + weight * 3.5,
          opacity: 0.2 + weight * 0.45,
          lineCap: 'round',
        })
        arrow.bindTooltip(`${f.source} → ${f.target}: ${fmt.format(f.value)} чел.`, { sticky: true })
        flowsLayer.addLayer(arrow)
      }
      for (const [name, coord] of Object.entries(meta.fo_centroids)) {
        const marker = L.circleMarker([coord[1], coord[0]], {
          radius: 8,
          color: '#101820',
          weight: 2,
          fillColor: '#7ec8c4',
          fillOpacity: 1,
        })
        marker.bindTooltip(name, { direction: 'top', offset: [0, -6] })
        marker.on('click', () => {
          state.selected = name
          renderDetail(name)
        })
        flowsLayer.addLayer(marker)
      }
    }
    if (particleAnim) {
      particleAnim.setPaths(paths)
      particleAnim.start()
    }
  }
}

function bindUI() {
  document.querySelectorAll('.mode-switch button').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.mode = (btn as HTMLElement).dataset.mode as Mode
      state.selected = null
      // Jump to a sensible default year for the mode
      const years = yearsForMode(state.mode)
      state.year = years.includes(2022) ? 2022 : years[years.length - 1]
      stopPlay()
      syncControls()
    })
  })

  const slider = document.querySelector('#year') as HTMLInputElement
  slider.addEventListener('input', () => {
    const years = yearsForMode(state.mode)
    state.year = years[Number(slider.value)]
    stopPlay()
    syncControls()
  })

  document.querySelector('#play')!.addEventListener('click', () => {
    if (state.playing) stopPlay()
    else startPlay()
  })

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDetail()
  })
}

function startPlay() {
  state.playing = true
  ;(document.querySelector('#play') as HTMLButtonElement).textContent = '❚❚'
  playTimer = window.setInterval(() => {
    const years = yearsForMode(state.mode)
    const idx = years.indexOf(state.year)
    state.year = years[(idx + 1) % years.length]
    syncControls()
  }, 900)
}

function stopPlay() {
  state.playing = false
  ;(document.querySelector('#play') as HTMLButtonElement).textContent = '▶'
  if (playTimer != null) {
    clearInterval(playTimer)
    playTimer = null
  }
}

function fitRussia() {
  if (!map || !regionsLayer) return
  map.invalidateSize()
  map.fitBounds(regionsLayer.getBounds().pad(0.04), {
    paddingTopLeft: [6, 84],
    paddingBottomRight: [6, 84],
    animate: false,
  })
}

function initMap() {
  map = L.map('map', {
    center: [64, 110],
    zoom: 2.5,
    minZoom: 1,
    maxZoom: 10,
    zoomControl: false,
    attributionControl: true,
    worldCopyJump: false,
  })
  L.control.zoom({ position: 'bottomright' }).addTo(map)

  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: '© OpenStreetMap © CARTO',
    subdomains: 'abcd',
    maxZoom: 18,
    opacity: 0.5,
    // Keep world from wrapping under Far East polygons that sit past lon 180
    noWrap: true,
    bounds: [
      [-85, -20],
      [85, 200],
    ],
  }).addTo(map)

  particleAnim = new ParticleAnimation(map)

  const mapEl = document.querySelector('#map') as HTMLElement
  mapEl.style.zIndex = '1'

  syncControls()
  // fitBounds after layout settles — otherwise Far East is clipped on first paint
  map.whenReady(() => {
    fitRussia()
    setTimeout(fitRussia, 150)
    setTimeout(fitRussia, 450)
  })
  window.addEventListener('resize', () => {
    map.invalidateSize()
  })
}

async function main() {
  renderShell()
  meta = await loadJSON<Meta>('/data/meta.json')
  cityByYear = await loadJSON<Record<string, RegionMetric[]>>('/data/city_change_by_year.json')
  migrationByYear = await loadJSON<Record<string, RegionMetric[]>>('/data/migration_net_by_year.json')
  foFlows = await loadJSON<Record<string, FlowYear>>('/data/fo_flows.json')
  timeline = await loadJSON<TimelinePoint[]>('/data/national_timeline.json')
  subjects = (await loadJSON<{ subjects: Record<string, unknown> }>('/data/subjects.json')).subjects
  cityPointsByYear = await loadJSON<Record<string, CityPoint[]>>('/data/city_points_by_year.json')
  regionsGeo = await loadJSON<FeatureCollection>('/geo/russia-regions.geojson')
  state.year = 2023
  state.mode = 'migration'
  bindUI()
  initMap()
}

main().catch((err) => {
  console.error(err)
  document.body.innerHTML = `<pre style="color:#fff;padding:2rem">Ошибка загрузки атласа: ${String(err)}</pre>`
})
